import {
    useCustomerKicoinQuery,
    useGetDiscountKicoinsQuery,
    useRemoveKicoinsFromCartMutation,
    useApplyKicoinsToCartMutation,
    useCustomerCartLazyQuery,
    useCartTotalLazyQuery,
    useCartTotalQuery as useCartTotalQueryGenerated,
    useCartItemsStatusQuery,
    useMergeCartsMutation,
    useAssignCustomerToGuestCartMutation,
    useCartItemsLazyQuery,
    useFastCartTotalsLazyQuery as useFastCartTotalsLazyQueryGenerated,
    CartTotalDocument,
    FastCartTotalsDocument,
} from '../../types/front.graphql';

import type { ApolloError } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { logSentry, useMagentoGraphQlUrl } from '@kidslabs/module-core';

export function useCartId() {
    const [getCustomerCartQuery, { data, loading, error }] = useCustomerCartLazyQuery({
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        if (error) {
            console.log('useCartId error', error);
            logSentry({
                error: error,
                name: 'cart',
                transactionName: 'getCustomerCartQuery',
                extra: { err: error },
            });
        }
    }, [error]);

    return { getCustomerCartQuery, data, loading, error };
}

export function useCartItems() {
    const magentoGraphqlApiEndpointPublic = useMagentoGraphQlUrl();
    const [getCartItemsQuery, { data, loading, error }] = useCartItemsLazyQuery({
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        if (error) {
            logSentry({
                error: error,
                name: 'cart',
                transactionName: 'getCartItems',
                extra: { err: error },
            });
        }
    }, [error]);

    const getCartItems = useCallback(
        (options?: Parameters<typeof getCartItemsQuery>[0]) => {
            return getCartItemsQuery({
                ...options,
                context: {
                    uri: magentoGraphqlApiEndpointPublic,
                    ...options?.context,
                },
            });
        },
        [getCartItemsQuery, magentoGraphqlApiEndpointPublic]
    );

    return { getCartItems, data, loading, error };
}

export function useCartItemsStatus(cartId: string) {
    const { data, loading, error } = useCartItemsStatusQuery({
        variables: {
            cartId,
        },
    });

    return { items: data?.cartItemsStatus ?? [], loading, error };
}

export function useCartTotal() {
    const magentoGraphqlApiEndpointPublic = useMagentoGraphQlUrl();
    const [fetchCartTotalQuery, { data, loading, error, refetch }] = useCartTotalLazyQuery({
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        if (error) {
            logSentry({
                error: error,
                name: 'cart',
                transactionName: 'fetchCartTotalQuery',
                extra: { err: error },
            });
        }
    }, [error]);

    const fetchCartTotal = async (cartId: string) => {
        const response = await fetchCartTotalQuery({
            variables: {
                cartId,
            },
            context: {
                uri: magentoGraphqlApiEndpointPublic,
            },
        });
        return response.data?.cart;
    };

    return { fetchCartTotal, data, loading, error, refetch };
}

// Kicoins
export function useCustomerKicoins() {
    const { data, loading, error } = useCustomerKicoinQuery({
        onCompleted: () => {},
        onError: (err: ApolloError) => {
            logSentry({
                error: err,
                name: 'kicoins',
                transactionName: 'useCustomerKicoins',
                extra: { err },
            });
        },
    });

    return { data, loading, error };
}

export function useGetDiscountKicoins(cartId: string) {
    const { data, loading, error } = useGetDiscountKicoinsQuery({
        variables: {
            cartId,
        },
        onCompleted: () => {},
        onError: (err: ApolloError) => {
            logSentry({
                error: err,
                name: 'kicoins',
                transactionName: 'useGetDiscountKicoins',
                extra: { err },
            });
        },
    });

    return { data, loading, error };
}

export function useRemoveKicoinsFromCart() {
    const [removeKicoinsFromCartMutation, { loading, error, data }] = useRemoveKicoinsFromCartMutation({
        onCompleted: () => {},
        onError: (apolloError: ApolloError) => {
            logSentry({
                error: apolloError,
                name: 'kicoins',
                transactionName: 'useRemoveKicoinsFromCart',
                extra: { apolloError },
            });
        },
    });
    const removeKicoinsFromCart = (cartId: string) => {
        return removeKicoinsFromCartMutation({
            refetchQueries: ['CartTotal', 'GetDiscountKicoins'],
            variables: {
                input: {
                    cart_id: cartId,
                },
            },
        });
    };

    return {
        removeKicoinsFromCart,
        loading,
        error,
        data,
    };
}

export function useApplyKicoinsToCart() {
    const [applyKicoinsToCartMutation, { loading, error, data }] = useApplyKicoinsToCartMutation({
        onCompleted: () => {},
        onError: (apolloError: ApolloError) => {
            logSentry({
                error: apolloError,
                name: 'kicoins',
                transactionName: 'useApplyKicoinsToCart',
                extra: { apolloError },
            });
        },
    });
    const applyKicoinsToCart = (cartId: string, onError?: (error: ApolloError) => void) => {
        return applyKicoinsToCartMutation({
            refetchQueries: ['CartTotal', 'GetDiscountKicoins'],
            variables: {
                input: {
                    cart_id: cartId,
                },
            },
            onError,
        });
    };

    return {
        applyKicoinsToCart,
        loading,
        error,
        data,
    };
}

export function useMergeCart() {
    const [mergeCartsMutation, { loading, error, data }] = useMergeCartsMutation();
    const mergeCarts = async (sourceCartId: string, destinationCartId: string) => {
        const result = await mergeCartsMutation({
            variables: {
                sourceCartId,
                destinationCartId,
            },
        });
        return result?.data?.mergeCarts?.id;
    };

    return {
        mergeCarts,
        loading,
        error,
        data,
    };
}

/**
 * Assigns the currently authenticated customer to an existing guest cart.
 * This replaces mergeCarts for the post-login flow: instead of creating a new
 * customer cart and merging items, it directly converts the guest cart into
 * the customer's cart, preserving the same cart ID and all items/rules.
 */
export function useAssignCustomerToGuestCart() {
    const [assignCustomerToGuestCartMutation, { loading, error, data }] = useAssignCustomerToGuestCartMutation();

    const assignCustomerToGuestCart = useCallback(
        async (guestCartId: string): Promise<string | undefined> => {
            try {
                const result = await assignCustomerToGuestCartMutation({
                    variables: { cartId: guestCartId },
                });
                return result?.data?.assignCustomerToGuestCart?.id;
            } catch (assignError) {
                logSentry({
                    error: assignError instanceof Error ? assignError : new Error(String(assignError)),
                    name: 'useAssignCustomerToGuestCart',
                    transactionName: 'assignCustomerToGuestCart',
                    extra: { guestCartId },
                });
                throw assignError;
            }
        },
        [assignCustomerToGuestCartMutation],
    );

    return {
        assignCustomerToGuestCart,
        loading,
        error,
        data,
    };
}

/**
 * Fast cart totals hook - lightweight totals without full cart data
 * Use this when you only need totals, not full cart items
 */
export function useFastCartTotals() {
    const magentoGraphqlApiEndpointPublic = useMagentoGraphQlUrl();
    const [fetchFastCartTotalsQuery, { data, loading, error, refetch }] = useFastCartTotalsLazyQueryGenerated({
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        if (error) {
            logSentry({
                error: error,
                name: 'cart',
                transactionName: 'fetchFastCartTotals',
                extra: { err: error },
            });
        }
    }, [error]);

    const fetchTotals = async (cartId: string) => {
        const response = await fetchFastCartTotalsQuery({
            variables: {
                cartId,
            },
            context: {
                uri: magentoGraphqlApiEndpointPublic,
            },
        });
        return response.data?.fastCartTotals;
    };

    return {
        fetchFastCartTotals: fetchTotals,
        data,
        loading,
        error,
        refetch,
    };
}

/**
 * Hook to get cart total details with proper context and error handling
 * Used by checkout module to display order summary
 */
export function useCartTotalDetails(cartId: string, options?: { skip?: boolean }) {
    const magentoGraphqlApiEndpointPublic = useMagentoGraphQlUrl();

    const { data, loading, error, refetch } = useCartTotalQueryGenerated({
        variables: { cartId },
        skip: options?.skip || !cartId,
        fetchPolicy: 'cache-and-network',
        onError: err => {
            logSentry({
                error: err,
                name: 'cart',
                transactionName: 'useCartTotalDetails',
                extra: { err, cartId },
            });
        },
        context: {
            uri: magentoGraphqlApiEndpointPublic,
        },
    });

    return {
        cart: data?.cart,
        loading,
        error,
        refetch,
    };
}

/**
 * Hook to fetch cart totals lazily with fast endpoint
 * Useful for refetching totals after cart operations
 */
export function useFetchCartTotalsLazy() {
    const magentoGraphqlApiEndpointPublic = useMagentoGraphQlUrl();

    const [fetchCartTotalsLazy, { data, loading, error }] = useFastCartTotalsLazyQueryGenerated({
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (error) {
            logSentry({
                error: error,
                name: 'cart',
                transactionName: 'useFetchCartTotalsLazy',
                extra: { err: error },
            });
        }
    }, [error]);

    const fetchTotals = useCallback(
        async (cartId: string) => {
            const result = await fetchCartTotalsLazy({
                variables: { cartId },
                context: {
                    uri: magentoGraphqlApiEndpointPublic,
                },
            });
            return result.data?.fastCartTotals;
        },
        [fetchCartTotalsLazy, magentoGraphqlApiEndpointPublic]
    );

    return {
        fetchTotals,
        totals: data?.fastCartTotals,
        loading,
        error,
    };
}

// Re-export documents for refetchQueries usage
export { CartTotalDocument, FastCartTotalsDocument };
